// src/components/BuddyToolList.js
import React, { useState, useMemo, useEffect } from 'react';
import './BuddyToolList.css';
import BuddyToolCard from './BuddyToolCard';
import { getVisibleTools } from '../data/buddyTools';
import WelcomeWidget from './WelcomeWidget';
import { getBranding } from '../scripts/branding';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

// Icons
const GridIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>;
const ListIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>;

const BuddyToolList = ({ tools: toolsFromProps, isPublicPage = false }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState(isPublicPage ? 'alphabetical' : 'favorites');
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(() => {
    try {
      const savedFavorites = localStorage.getItem('buddyToolFavorites');
      return savedFavorites ? JSON.parse(savedFavorites) : [];
    } catch (error) {
      console.error("Failed to parse favorites from localStorage", error);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('buddyToolFavorites', JSON.stringify(favorites));
  }, [favorites]);

  const handleFavoriteToggle = (toolId) => {
    setFavorites(prevFavorites =>
      prevFavorites.includes(toolId)
        ? prevFavorites.filter(id => id !== toolId)
        : [...prevFavorites, toolId]
    );
  };

  const filteredAndSortedTools = useMemo(() => {
    const sourceTools = toolsFromProps || getVisibleTools();
    const filtered = sourceTools.filter(tool =>
      tool.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      if (!isPublicPage) {
        const aIsFavorite = favorites.includes(a.id);
        const bIsFavorite = favorites.includes(b.id);
        if (sortOrder === 'favorites') {
          if (aIsFavorite && !bIsFavorite) return -1;
          if (!aIsFavorite && bIsFavorite) return 1;
        }
      }
      if (sortOrder === 'alphabetical' || sortOrder === 'favorites') {
        return a.title.localeCompare(b.title);
      } else if (sortOrder === 'reverse-alphabetical') {
        return b.title.localeCompare(a.title);
      }
      return 0;
    });
  }, [sortOrder, favorites, searchTerm, toolsFromProps, isPublicPage]);

  const branding = getBranding();
  const toolsetTitle = branding.toolName === 'Armory' ? 'Your Toolkit' : 'BuddyTools';

  return (
    <div className="buddy-tool-list-container">
      {!isPublicPage && <WelcomeWidget />}

      <div className="buddy-tool-list-header">
        {!isPublicPage && <h2>{toolsetTitle}</h2>}
        <div className={`toolbar ${isPublicPage ? 'public' : ''}`}>
          <input
            type="text"
            placeholder="Search tools..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!isPublicPage && (
            <>
              <div className="view-mode-toggle">
                <button onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'active' : ''}><GridIcon /></button>
                <button onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'active' : ''}><ListIcon /></button>
              </div>
              <div className="sort-options">
                <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                  <option value="favorites">Sort by Favorites</option>
                  <option value="alphabetical">Sort A-Z</option>
                  <option value="reverse-alphabetical">Sort Z-A</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={viewMode === 'grid' ? 'buddy-tool-grid' : 'buddy-tool-list'}>
        {filteredAndSortedTools.map(tool => (
          <BuddyToolCard
            key={tool.id}
            tool={tool}
            viewMode={viewMode}
            isFavorite={favorites.includes(tool.id)}
            onFavoriteToggle={handleFavoriteToggle}
            isPublicPage={isPublicPage}
          />
        ))}
      </div>
      <Tooltip id="buddy-tooltip" />
    </div>
  );
};

export default BuddyToolList;
